import { LoadingOutlined } from "@ant-design/icons";
import { Alert, Space, Typography } from "antd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

const { Text } = Typography;

export type OperationLoadingBannerPreset = "instanceAccess" | "financeReport";

export type OperationLoadingBannerProps = {
  /** When false, nothing is rendered. Defaults to true. */
  show?: boolean;
  /** Built-in copy for common long operations. Ignored when message/description are provided. */
  preset?: OperationLoadingBannerPreset;
  message?: ReactNode;
  description?: ReactNode;
  style?: React.CSSProperties;
};

const presetCopy = (
  preset: OperationLoadingBannerPreset,
  t: TFunction<"translation">
): { message: ReactNode; description: ReactNode } => {
  switch (preset) {
    case "financeReport":
      return {
        message: t("Operation_in_Progress"),
        description: (
          <Space direction="vertical" size="small">
            <Text>{t("Generating_Report_Please_Wait")}</Text>
            <Text strong>{t("Do_Not_Close_Page")}</Text>
          </Space>
        ),
      };
    case "instanceAccess":
    default:
      return {
        message: t("Operation_in_Progress"),
        description: (
          <Space direction="vertical" size="small">
            <Text>{t("Granting_Access_Please_Wait")}</Text>
            <Text strong>{t("Do_Not_Close_Page")}</Text>
          </Space>
        ),
      };
  }
};

/**
 * Banner for long-running operations (report generation, bulk access updates).
 * Use with fetcher or action busy state — not for route navigation or table refresh.
 */
export const OperationLoadingBanner = ({
  show = true,
  preset = "instanceAccess",
  message,
  description,
  style,
}: OperationLoadingBannerProps) => {
  const { t } = useTranslation();

  if (!show) {
    return null;
  }

  const copy = presetCopy(preset, t);

  return (
    <Alert
      type="info"
      banner
      message={message ?? copy.message}
      description={description ?? copy.description}
      icon={<LoadingOutlined />}
      showIcon
      style={{ marginBottom: 16, ...style }}
    />
  );
};
